import { type ChangeEvent, useId, useState } from "react";
import { formatPriceCents } from "../../lib/money";
import { MoneyInput } from "../../components/MoneyInput";
import {
	lineItemSubtotalCents,
	parseQuantity,
	quantityToInput,
} from "./costing";
import { emptyLineItem, type LineItemDraft } from "./types";
import styles from "./LineItemsField.module.css";

export interface LineItemsFieldProps {
	/** Current line items; edits flow up through `onChange` whole-list replaces. */
	items: readonly LineItemDraft[];
	onChange: (items: LineItemDraft[]) => void;
	/**
	 * What an empty list means here. The consequence of adding no rows differs
	 * by host — a job falls back to its price estimate, an invoice cannot be
	 * created at all — so the host says it rather than the field guessing.
	 */
	emptyMessage?: string;
}

/**
 * Repeatable priced line items for the Scope page.
 *
 * Presentational and reusable: it owns no form state. Each row is keyed by its
 * item id, so per-row input text survives re-renders and list edits, and the
 * item subtotal is always derived (quantity × unit amount), never typed.
 *
 * The rows are a column layout, not a stack of self-contained cards: title,
 * quantity, unit amount and subtotal line up between rows so a list of prices
 * can be read down instead of field-by-field. Each row still carries real
 * labels — they are hidden only while the column headings are on screen to say
 * the same thing, and come back when the layout stacks.
 */
export function LineItemsField({
	items,
	onChange,
	emptyMessage = "No line items yet.",
}: LineItemsFieldProps) {
	const addItem = (): void => onChange([...items, emptyLineItem()]);

	const updateItem = (id: string, next: Partial<LineItemDraft>): void =>
		onChange(
			items.map((item) => (item.id === id ? { ...item, ...next } : item)),
		);

	const removeItem = (id: string): void =>
		onChange(items.filter((item) => item.id !== id));

	return (
		<div className={styles.root}>
			{items.length === 0 ? (
				<div className={styles.empty}>
					<p className={styles.emptyText}>{emptyMessage}</p>
					<button type="button" className={styles.addEmpty} onClick={addItem}>
						Add line item
					</button>
				</div>
			) : (
				<div className={styles.list}>
					<div className={styles.head} aria-hidden="true">
						<span className={styles.headNum}>#</span>
						<span className={styles.headCell}>Title</span>
						<span className={styles.headNumeric}>Qty</span>
						{/* "Unit $" rather than "Unit amount": the column is sized to its
						    values, and the full phrase wrapped to two lines there. The
						    fields keep the spelt-out accessible name. */}
						<span className={styles.headNumeric}>Unit $</span>
						<span className={styles.headAmount}>Subtotal</span>
						<span />
					</div>
					<ul className={styles.items}>
						{items.map((item, index) => (
							<LineItemRow
								key={item.id}
								item={item}
								index={index}
								onChange={(next) => updateItem(item.id, next)}
								onRemove={() => removeItem(item.id)}
							/>
						))}
					</ul>
				</div>
			)}

			{items.length > 0 && (
				<div className={styles.footer}>
					<button type="button" className={styles.add} onClick={addItem}>
						<span className={styles.plus} aria-hidden="true">
							+
						</span>
						Add line item
					</button>
					<span className={styles.count}>
						{items.length} item{items.length === 1 ? "" : "s"}
					</span>
				</div>
			)}
		</div>
	);
}

interface LineItemRowProps {
	item: LineItemDraft;
	/** 0-based position, shown as a human "Item N" heading. */
	index: number;
	onChange: (next: Partial<LineItemDraft>) => void;
	onRemove: () => void;
}

/** One line item: title, description, quantity, unit amount, derived subtotal. */
function LineItemRow({ item, index, onChange, onRemove }: LineItemRowProps) {
	const [quantityText, setQuantityText] = useState(() =>
		quantityToInput(item.quantity),
	);
	// The description is optional detail on most rows, so it opens on demand and
	// stays open for any row that already carries text — a filled field is never
	// hidden behind a disclosure.
	const [detailRequested, setDetailRequested] = useState(false);
	const hintId = useId();

	const hasDescription = item.description.trim().length > 0;
	const detailOpen = detailRequested || hasDescription;

	// Mirrors validateJobScopeDraft: a row only has to be coherent once it is
	// priced, so an untouched row is never marked wrong.
	const subtotalCents = lineItemSubtotalCents(item);
	const priced = item.unitAmountCents > 0;
	const titleMissing = priced && !item.title.trim();
	const quantityMissing = priced && !(item.quantity > 0);
	const hint = titleMissing
		? "Give this item a title, or clear its amount."
		: quantityMissing
			? "Quantity must be greater than zero."
			: "";

	const handleTitleChange = (event: ChangeEvent<HTMLInputElement>): void =>
		onChange({ title: event.target.value });
	const handleDescriptionChange = (
		event: ChangeEvent<HTMLTextAreaElement>,
	): void => onChange({ description: event.target.value });
	const handleQuantityChange = (event: ChangeEvent<HTMLInputElement>): void => {
		const text = event.target.value;
		setQuantityText(text);
		onChange({ quantity: parseQuantity(text) });
	};
	const handleUnitAmountChange = (unitAmountCents: number): void =>
		onChange({ unitAmountCents });

	return (
		<li className={styles.item}>
			<span className={styles.num} aria-hidden="true">
				{index + 1}
			</span>

			<label className={styles.cellTitle}>
				<span className={styles.cellLabel}>Item {index + 1} title</span>
				<input
					className={styles.titleInput}
					name={`lineItemTitle-${item.id}`}
					value={item.title}
					placeholder="e.g. Replace tap washer"
					aria-invalid={titleMissing || undefined}
					aria-describedby={titleMissing ? hintId : undefined}
					onChange={handleTitleChange}
				/>
			</label>

			<label className={styles.cell}>
				<span className={styles.cellLabel}>Quantity</span>
				<input
					className={styles.numberInput}
					name={`lineItemQuantity-${item.id}`}
					type="text"
					inputMode="decimal"
					value={quantityText}
					placeholder="1"
					aria-invalid={quantityMissing || undefined}
					aria-describedby={quantityMissing ? hintId : undefined}
					onChange={handleQuantityChange}
				/>
			</label>

			{/*
			 * A <div>, not a <label>: the control lives inside MoneyInput, so a
			 * wrapping label cannot be associated with it. The field carries its
			 * own aria-label, and the span below is the column's visible name for
			 * the stacked layout.
			 */}
			<div className={styles.cell}>
				<span className={styles.cellLabel}>Unit amount</span>
				<MoneyInput
					valueCents={item.unitAmountCents}
					onChange={handleUnitAmountChange}
					name={`lineItemUnitAmount-${item.id}`}
					ariaLabel={`Unit amount for item ${index + 1}`}
					variant="inline"
				/>
			</div>

			<div className={styles.amountCell}>
				<span className={styles.cellLabel}>Subtotal</span>
				{/* A <span>, not an <output>: <output> is a live region, so every
            keystroke in the amount field would be announced mid-typing. */}
				<span className={styles.subtotal} data-zero={subtotalCents === 0}>
					{formatPriceCents(subtotalCents)}
				</span>
			</div>

			<button
				type="button"
				className={styles.remove}
				onClick={onRemove}
				title={`Remove item ${index + 1}`}
				aria-label={`Remove item ${index + 1}`}
			>
				<svg
					viewBox="0 0 16 16"
					width="15"
					height="15"
					aria-hidden="true"
					focusable="false"
				>
					<path
						d="M3 4.5h10M6.5 4.5V3.2h3v1.3M4.6 4.5l.5 8.1h5.8l.5-8.1M6.7 6.8v3.6M9.3 6.8v3.6"
						fill="none"
						stroke="currentColor"
						strokeWidth="1.2"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
				</svg>
			</button>

			{detailOpen ? (
				<label className={styles.detail}>
					<span className={styles.detailLabelRow}>
						<span className={styles.detailLabel}>Item description</span>
						{!hasDescription && (
							<button
								type="button"
								className={styles.detailToggle}
								onClick={() => setDetailRequested(false)}
							>
								Hide
							</button>
						)}
					</span>
					<textarea
						className={styles.detailInput}
						name={`lineItemDescription-${item.id}`}
						rows={2}
						value={item.description}
						placeholder="Optional detail for this line"
						onChange={handleDescriptionChange}
					/>
				</label>
			) : (
				<div className={styles.detail}>
					<button
						type="button"
						className={styles.detailToggle}
						onClick={() => setDetailRequested(true)}
					>
						<span className={styles.plus} aria-hidden="true">
							+
						</span>
						Add description
					</button>
				</div>
			)}

			{hint && (
				<p className={styles.hint} id={hintId}>
					{hint}
				</p>
			)}
		</li>
	);
}
